/**
 * V3.4 (2026-06-03) — onboarding state machine.
 *
 * Single source of truth for "where does this user belong right now?".
 * Every /onboarding/* page (and the JourneyBar) calls this with the
 * `/auth/me` payload + the current pathname. The function returns
 * either:
 *
 *   - null            → caller is already on the right page, no redirect
 *   - a target path   → redirect the caller there
 *
 * The previous V2.5 logic was inline-per-page and produced infinite
 * loops (e.g. /onboarding/apply bounced pending users back to /role,
 * which bounced them back to /apply because role was 'economist' but
 * application was incomplete). This helper makes the state graph
 * explicit and testable.
 *
 * State graph (anchor: Leon 2026-06-03 directive in CLAUDE.md):
 *
 *   role='pending', no display_name                  → /onboarding/name
 *   role='pending', no application                   → /onboarding/role
 *   role='pending', application='pending_review' or 'pending'
 *                                                    → /onboarding/waiting
 *   role='pending', application='rejected'           → /onboarding/waiting
 *   role='pending', application='approved'           → /studio (race window)
 *   role='consumer'                                  → /discover
 *   role='economist'                                 → /studio (overview)
 *                                                      (pathway / vault /
 *                                                      train decisions
 *                                                      live on /studio
 *                                                      itself)
 *   role='platform_admin'                            → /platform
 *
 * Per Leon: setup tabs (concepts/train/persona/studio/launch) are
 * VISIBLE during onboarding but gated — the TopBar grays them out and
 * routes the user back to the right step on click. So once role hits
 * 'economist', we stop redirecting them from /studio/* routes; they
 * can self-navigate. The only forced redirect after approval is the
 * /onboarding/* shell itself, which sends them to /studio.
 */

export type MeForStepping = {
  role: string;
  display_name: string | null;
  application_status?: string | null;
  application_branch?: string | null;
};

/**
 * Where should this user be sent right now? Returns null if no redirect
 * is needed for the given pathname (caller is already on their step).
 *
 * Pages should call this BEFORE rendering and use the return value with
 * next/navigation's redirect(). Pathname is the requested URL — we pass
 * it explicitly because the helper must NOT pull `usePathname` (it's
 * called from server components).
 */
export function nextOnboardingStep(me: MeForStepping | null, pathname: string): string | null {
  // Unauthenticated → /login (caller layer normally enforces this; we
  // also handle it so the helper is total).
  if (!me) return '/login';

  const role = me.role;
  const hasName = !!me.display_name?.trim();
  const appStatus = me.application_status ?? null;

  // platform_admin & consumer have stable homes; if they wander into
  // /onboarding/* we kick them out.
  if (role === 'platform_admin') {
    // V3.4 (2026-06-03n) — admin can navigate to 入驻信息 from the
    // profile menu. Carve out the same allowList economists get so
    // the link actually lands; the page itself renders whatever
    // (read-only) data exists for the admin account.
    const adminAllow = new Set(['/onboarding/role', '/onboarding/apply', '/onboarding/waiting']);
    if (adminAllow.has(pathname)) return null;
    return pathname.startsWith('/onboarding') ? '/platform' : null;
  }
  if (role === 'consumer') {
    return pathname.startsWith('/onboarding') ? '/discover' : null;
  }

  // Economist (approved): stop blocking — they own /studio. /onboarding/
  // pathway IS the post-approval "select your starting point" step and
  // stays open.
  // V3.4 (2026-06-03c) — Leon: approved economists should also be able
  // to VIEW their settled onboarding choice (locked, with a notice
  // pointing at platform admin for changes). So /onboarding/role,
  // /onboarding/apply and /onboarding/waiting are also allowed; the
  // pages render in read-only mode when role==='economist'.
  if (role === 'economist') {
    const allowList = new Set([
      '/onboarding/pathway',
      '/onboarding/role',
      '/onboarding/apply',
      '/onboarding/waiting',
    ]);
    if (allowList.has(pathname)) return null;
    return pathname.startsWith('/onboarding') ? '/studio' : null;
  }

  // From here down, role === 'pending'. The four sub-states:

  // (1) No display_name yet → /onboarding/name (handles first-time
  //     post-magic-link verify).
  if (!hasName) {
    return pathname === '/onboarding/name' ? null : '/onboarding/name';
  }

  // (2) Has name, no application yet → /onboarding/role (the new
  //     welcome screen with 8 specialty cards).
  if (!appStatus) {
    return pathname === '/onboarding/role' ? null : '/onboarding/role';
  }

  // (3) Application is pending_review (V3.4) or pending (legacy) →
  //     waiting holding page. Allow staying on /apply too, so a user
  //     who wants to edit credentials before approval can.
  if (appStatus === 'pending_review' || appStatus === 'pending') {
    if (pathname === '/onboarding/waiting') return null;
    if (pathname === '/onboarding/apply') return null;
    return '/onboarding/waiting';
  }

  // (4) Rejected → waiting page surfaces the reason + a contact link.
  if (appStatus === 'rejected') {
    return pathname === '/onboarding/waiting' ? null : '/onboarding/waiting';
  }

  // (5) Approved but role somehow still 'pending' — race window. Force
  //     a /studio land which will re-read the cookie.
  if (appStatus === 'approved') {
    return '/studio';
  }

  // Unknown application status — degrade to welcome.
  return pathname === '/onboarding/role' ? null : '/onboarding/role';
}

/**
 * Home for a finalised role. Use after the step machine says "the user
 * is done with onboarding" and you need a default landing URL.
 */
export function homeForRole(role: string): string {
  if (role === 'platform_admin') return '/platform';
  if (role === 'consumer') return '/discover';
  if (role === 'economist') return '/studio';
  return '/onboarding/role';
}
